/**
 * 98. Tenant Settings — company details; localization; fiscal year; backup;
 * export; environment (sandbox/production) info.
 *
 * Arabic and English (C7): every word from the catalogs; the environment,
 * the workspace's status and its plan are words, never the stored value; the
 * country is named in the interface's language.
 *
 * Read only until saving exists (UI-HONEST). There is no
 * `PATCH /v1/tenants/settings` yet (roadmap F10; F10-S, then this screen in
 * F10-W), so the fields no longer look editable and Save says what it waits
 * for; the customer data export is D2-W. Start backup and Purge were
 * removed: per-workspace backups and purge are run by Atlas's own staff
 * through the operator console, with two people (operator-console.md OC-16,
 * OC-19), never from the customer's settings.
 */
import {
  Card,
  PageHeader,
  Badge,
  Field,
  Input,
  Select,
  Skeleton,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiQuery } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { statusLabel, valueLabel } from "@/lib/i18n/records";
import { countryName } from "@/lib/reference";

interface TenantSettings {
  name: string;
  code: string;
  status: "Provisioning" | "Active" | "Suspended" | "LegalHold" | "Closed";
  planTier: string;
  defaultCurrencyCode: string;
  defaultCountryCode: string;
  timezone: string;
  locale: string;
  dataResidencyRegion: string;
  isolationMode: string;
  environment: "Sandbox" | "Production";
}

export function TenantSettingsPage() {
  const { t, i18n } = useTranslation();
  const { data, isLoading } = useApiQuery<TenantSettings>(
    ["tenants", "settings"],
    "/tenants/settings",
  );

  if (isLoading || !data) return <Skeleton rows={10} />;

  return (
    <div>
      <PageHeader
        title={t("admin.tenant.title")}
        subtitle={t("admin.tenant.subtitle")}
        actions={
          <PendingButton variant="primary" reason="settingsSave" slice="F10-W">
            {t("admin.tenant.save")}
          </PendingButton>
        }
      />
      <div className="mb-4">
        <Badge tone={data.environment === "Production" ? "success" : "primary"}>
          {t("admin.tenant.environment", {
            value: valueLabel(t, "environment", data.environment),
          })}
        </Badge>{" "}
        <Badge tone={data.status === "Active" ? "success" : "default"}>
          {t("admin.tenant.status", {
            value: statusLabel(t, "Workspace", data.status),
          })}
        </Badge>{" "}
        <Badge tone="default">
          {t("admin.tenant.plan", {
            value: valueLabel(t, "planTier", data.planTier),
          })}
        </Badge>
      </div>
      <div className="atlas-grid atlas-grid--2">
        <Card title={t("admin.tenant.company")}>
          <Field label={t("admin.tenant.companyName")}>
            <Input defaultValue={data.name} readOnly />
          </Field>
          <Field label={t("admin.tenant.tenantCode")}>
            <Input defaultValue={data.code} readOnly />
          </Field>
          <Field label={t("fields.country")}>
            <Select defaultValue={data.defaultCountryCode} disabled>
              <option value={data.defaultCountryCode}>
                {countryName(data.defaultCountryCode, i18n.language)}
              </option>
            </Select>
          </Field>
          <Field label={t("fields.currency")}>
            <Select defaultValue={data.defaultCurrencyCode} disabled>
              <option>{data.defaultCurrencyCode}</option>
            </Select>
          </Field>
        </Card>
        <Card title={t("admin.tenant.localization")}>
          <Field label={t("fields.timezone")}>
            <Input defaultValue={data.timezone} readOnly dir="ltr" />
          </Field>
          <Field label={t("admin.tenant.locale")}>
            <Input defaultValue={data.locale} readOnly dir="ltr" />
          </Field>
          <Field label={t("admin.tenant.region")}>
            <Input defaultValue={data.dataResidencyRegion} readOnly dir="ltr" />
          </Field>
          <Field label={t("admin.tenant.isolation")}>
            <Input
              defaultValue={valueLabel(t, "isolationMode", data.isolationMode)}
              readOnly
            />
          </Field>
        </Card>
      </div>
      <Card title={t("admin.tenant.backup")} className="mt-4">
        <PendingButton reason="dataExport" slice="D2-W">
          {t("admin.tenant.exportAll")}
        </PendingButton>
      </Card>
    </div>
  );
}
